import { useI18n } from '@/hooks/useI18n';
import { formatInt, formatNumber } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { AnalyticsInsightModel, InsightComparison } from '@/lib/analyticsInsights';

export function InsightComparisons({ model }: { model: AnalyticsInsightModel }) {
  const { t, locale } = useI18n();
  const followers = model.comparisons.followers;
  const ratio = (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 1 });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-[16px] font-semibold tracking-tight text-text-1">
          {t('Ce qui distingue ces comptes', 'What distinguishes these accounts')}
        </h3>
        <span className="text-[12px] text-text-3">
          {t(
            `${formatInt(model.selected.length)} sélectionnés · ${formatInt(model.rest.length)} autres`,
            `${formatInt(model.selected.length)} selected · ${formatInt(model.rest.length)} others`,
          )}
        </span>
      </div>
      <div className="overflow-hidden rounded-[14px] border border-line-1 bg-bg-1">
        <div className="grid divide-y divide-line-1 md:grid-cols-3 md:divide-x md:divide-y-0">
          <Comparison
            title={t('Vues gagnées / Reel mesuré', 'Views gained / measured Reel')}
            values={model.comparisons.viewsPerReel}
          />
          <Comparison
            title={t('Reels mesurés / compte', 'Measured Reels / account')}
            values={model.comparisons.reelsPerAccount}
          />
          <Comparison
            title={t('Abonnés actuels · médiane', 'Current followers · median')}
            values={followers}
            description={t(
              `Renseignés : ${followers.selectedKnown}/${model.selected.length} sélectionnés · ${followers.restKnown}/${model.rest.length} autres.`,
              `Known: ${followers.selectedKnown}/${model.selected.length} selected · ${followers.restKnown}/${model.rest.length} others.`,
            )}
          />
        </div>
        <div className="border-t border-line-1 px-4 py-3 text-[12px] leading-5 text-text-3 sm:px-5">
          {model.rest.length === 0 ? (
            <p>
              {t(
                'Aucun autre compte mesuré à comparer pour ce seuil.',
                'No other measured account to compare at this threshold.',
              )}
            </p>
          ) : (
            <>
              {model.comparisons.viewsPerReel.ratio !== null &&
                model.comparisons.reelsPerAccount.ratio !== null && (
                  <p className="text-text-2">
                    {t(
                      `La sélection atteint ×${ratio(model.comparisons.viewsPerReel.ratio)} les vues par Reel et ×${ratio(model.comparisons.reelsPerAccount.ratio)} le nombre de Reels mesurés par compte.`,
                      `The selection has ×${ratio(model.comparisons.viewsPerReel.ratio)} the views per Reel and ×${ratio(model.comparisons.reelsPerAccount.ratio)} the measured Reels per account.`,
                    )}
                  </p>
                )}
              <p>
                {t(
                  'Écarts observés, sans conclusion de causalité. Les Reels mesurés incluent les anciennes publications suivies.',
                  'Observed differences, not proof of causation. Measured Reels include older tracked posts.',
                )}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Comparison({
  title,
  values,
  description,
}: {
  title: string;
  values: InsightComparison;
  description?: string;
}) {
  const { t, locale } = useI18n();
  const max = Math.max(values.selected ?? 0, values.rest ?? 0);
  return (
    <div className="min-w-0 px-4 py-4 sm:px-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-[12px] font-medium text-text-1">{title}</h4>
        {values.ratio !== null && (
          <span className="rounded bg-violet-soft px-1.5 py-0.5 text-[11px] tabular-nums text-violet-deep">
            ×{values.ratio.toLocaleString(locale, { maximumFractionDigits: 1 })}
          </span>
        )}
      </div>
      <dl className="space-y-2">
        {(['selected', 'rest'] as const).map((cohort) => {
          const value = values[cohort];
          return (
            <div
              key={cohort}
              className="grid grid-cols-[58px_minmax(0,1fr)_48px] items-center gap-2 text-[12px]"
            >
              <dt className="text-text-3">
                {cohort === 'selected' ? t('Sélection', 'Selected') : t('Autres', 'Others')}
              </dt>
              <div className="h-1.5 overflow-hidden rounded-sm bg-bg-3" aria-hidden="true">
                <div
                  className={cn(
                    'h-full rounded-sm',
                    cohort === 'selected' ? 'bg-violet' : 'bg-text-4',
                  )}
                  style={{ width: `${value !== null && max > 0 ? (value / max) * 100 : 0}%` }}
                />
              </div>
              <dd
                className="text-right font-medium tabular-nums text-text-1"
                title={
                  value === null
                    ? t('Donnée indisponible', 'Unavailable')
                    : value.toLocaleString(locale)
                }
              >
                {value === null ? '—' : formatNumber(Math.round(value * 10) / 10)}
              </dd>
            </div>
          );
        })}
      </dl>
      {description && <p className="mt-2 text-[11px] leading-4 text-text-3">{description}</p>}
    </div>
  );
}
